import { Box, Button, Container, Typography, useTheme } from "@mui/material";
import { isCancel } from "axios";
import { useCallback, useEffect, useState } from "react";

import {
  AppDialogActions,
  AppDialogContent,
  CircularProgressWithLabel,
  useCountdown,
} from "@dfp/react-components";
import { IconKey01, IconXCircle } from "@dfp/react-icons";
import { useAsyncCallback } from "@dfp/react-utils";

import {
  isAuthRequestedError,
  isPlatformRequestError,
} from "../errors/index.ts";
import { RequestAuthorization } from "../types/requestAuthorization.ts";
import { useTT } from "../useTT.ts";
import { UserFriendlyId } from "./UserFriendlyId.tsx";

type Props = RequestAuthorization & {
  onClickClose: (event: React.MouseEvent<HTMLButtonElement>) => void;
};

export function MobileKeyAuthDialog({
  errorAttributes: { requestId, userFriendlyId },
  onAuthError,
  onAuthorize,
  onClickClose,
}: Props) {
  const { tt } = useTT();
  const theme = useTheme();

  const [authState, setAuthState] = useState<
    "pending" | "expired" | "rejected"
  >("pending");

  const [expiryTimeStamp, setExpiryTimeStamp] = useState<number>(0);
  const counter = useCountdown(expiryTimeStamp);

  const rejected = authState === "rejected";
  const expired = authState === "expired";
  const pending = authState === "pending";

  const apiLongPoll = useCallback(
    async (signal: AbortSignal) => {
      // constant condition results in simpler code and prevents recursion

      while (true) {
        try {
          return await onAuthorize({
            authRequestId: requestId,
            authWaitForDone: true,
            signal,
          });
        } catch (err) {
          if (
            !isPlatformRequestError(err, 401, "RequestAuthorizationPending")
          ) {
            throw err;
          }
        }
      }
    },
    [onAuthorize, requestId],
  );

  useEffect(() => {
    // Fake it till you make it in https://expensa.atlassian.net/browse/FIDFX-2335
    setExpiryTimeStamp(Date.now() + 298 * 1000);

    const abortController = new AbortController();
    apiLongPoll(abortController.signal).catch((err) => {
      if (
        isPlatformRequestError(
          err,
          401,
          "RequestAuthorizationFailed",
          "AuthRequestExpired",
        )
      ) {
        setAuthState("expired");
      } else if (
        isPlatformRequestError(
          err,
          401,
          "RequestAuthorizationFailed",
          "AuthRequestRejected",
        )
      ) {
        setAuthState("rejected");
      } else if (!isCancel(err)) {
        onAuthError(err);
      }
    });
    return () => abortController.abort();
  }, [apiLongPoll, onAuthError]);

  const { callback: onClickResend, isLoading: isResendingCode } =
    useAsyncCallback(async () => {
      try {
        await onAuthorize();
      } catch (err) {
        if (!isAuthRequestedError(err)) {
          onAuthError(err);
        }
      }
      setAuthState("pending");
    });

  return (
    <Box>
      <AppDialogContent>
        <Box display={"flex"} flexDirection={"column"} gap={3}>
          {pending && (
            <Typography>{tt("dialog.MobileKeyAuth.description")}</Typography>
          )}

          <Container
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            {(expired || rejected) && (
              <IconXCircle
                color={theme.palette.error.main}
                width={60}
                height={60}
              />
            )}
            {pending && (
              <CircularProgressWithLabel
                size={64}
                thickness={2}
                value={counter / 3}
                slots={{
                  BackgroundCircularProgress: {
                    sx: {
                      color: (theme) => theme.palette.grey[200],
                    },
                  },
                }}
              >
                <IconKey01 fontSize={32} />
              </CircularProgressWithLabel>
            )}
          </Container>

          {expired && (
            <Typography textAlign={"center"}>
              {tt("dialog.MobileKeyAuth.expired")}
            </Typography>
          )}
          {rejected && (
            <Typography textAlign={"center"}>
              {tt("dialog.MobileKeyAuth.rejected")}
            </Typography>
          )}
          {pending && (
            <Typography>{tt("dialog.MobileKeyAuth.help")}</Typography>
          )}
          <UserFriendlyId userFriendlyId={userFriendlyId} />
        </Box>
      </AppDialogContent>
      <AppDialogActions>
        {expired && (
          <Button
            color="secondary"
            onClick={onClickResend}
            loading={isResendingCode}
          >
            {tt("dialog.btnTryAgain")}
          </Button>
        )}
        {rejected && (
          <Button color="secondary" onClick={onClickClose}>
            {tt("dialog.btnCloseAuth")}
          </Button>
        )}
      </AppDialogActions>
    </Box>
  );
}
